import { createContext, useContext, type ReactNode } from 'react';
import { GUEST_COLOR } from '@awwyeah/shared';
import { useUser } from '../auth';

/**
 * Who is playing, on a page that may have no household session at all (WS-23).
 *
 * The board components were written against `useUser()`, which is the right answer inside
 * the family chrome and the wrong one on `/play/:token` — a guest has no `PublicUser`, no
 * email, and no member colour. Rather than teach every board about guests, one context sits
 * above the guest page and `useCurrentPlayer()` prefers it; everywhere else the context is
 * absent and the hook falls through to the member session exactly as before.
 *
 * That inversion is deliberate: the guest page is the unusual caller, so it is the one that
 * carries the extra wiring. Nothing inside `RequireAuth` changes behaviour.
 */

export interface CurrentPlayer {
  id: string;
  name: string;
  color: string;
  isGuest: boolean;
}

const GuestPlayerContext = createContext<CurrentPlayer | null>(null);

/** A household member the guest page already knows by name (from the seat response). */
export interface KnownMember {
  id: string;
  name: string;
  color: string;
}

const KnownMembersContext = createContext<readonly KnownMember[]>([]);

export function GuestPlayerProvider({
  guestId,
  guestName,
  knownMembers = [],
  children,
}: {
  guestId: string;
  guestName: string;
  /**
   * Friend of the family: the member across the table, so a guest's board says "Dan's move"
   * rather than "Member's move" — a guest can never open the household doc to learn names.
   */
  knownMembers?: readonly KnownMember[];
  children: ReactNode;
}): JSX.Element {
  return (
    <GuestPlayerContext.Provider
      value={{ id: guestId, name: guestName, color: GUEST_COLOR, isGuest: true }}
    >
      <KnownMembersContext.Provider value={knownMembers}>{children}</KnownMembersContext.Provider>
    </GuestPlayerContext.Provider>
  );
}

/** Members a guest page was told about; empty everywhere else. */
export function useKnownMembers(): readonly KnownMember[] {
  return useContext(KnownMembersContext);
}

/**
 * The player this browser is, or `null` when it is neither a signed-in member nor a claimed
 * guest. Board components use this instead of `useUser()` so one component serves both.
 */
export function useCurrentPlayer(): CurrentPlayer | null {
  const guest = useContext(GuestPlayerContext);
  const { user } = useUser();
  if (guest) return guest;
  if (!user) return null;
  return { id: user.id, name: user.name, color: user.color, isGuest: false };
}
